// #region Imports
import { useMediaQuery } from "@mui/material";
import { styled, Theme } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import AddBoxIcon from "@mui/icons-material/AddBox";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import {
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_TableInstance as TableInstance,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_RowData as RowData,
} from "material-react-table";
import {
  MobileToolbarButtonsContainer,
  ScrollableTabs,
  TableTitleRow,
  ToolbarButtons,
} from "_shared/_views/tables/table-toolbar";
import { useCallback } from "react";
import { useNavigate } from "react-router";
import useSessionUser from "permissions/session/use-session-user";
import { PageTab } from "work/fee-schedule-templates/_views/pages/tabs";
// #endregion

// #region Styled Components
const TableTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const NewButton = styled(IconButton)(() => ({
  padding: 0,
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
// #endregion

// #region Props
type FeeScheduleToolbarProps<TData extends RowData> = {
  table: TableInstance<TData>;
  activeTab: PageTab;
};

export default function FeeScheduleToolbar<TData extends RowData>(props: Readonly<FeeScheduleToolbarProps<TData>>) {
  const { table, activeTab } = props;

  // #region Hooks
  const navigate = useNavigate();
  const sessionUser = useSessionUser();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  // #region Handlers
  const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: PageTab) => {
    navigate(`/fee-schedules/${newValue}`);
  }, [navigate]);

  // #region Render
  return (
    <TableTopToolbarContainer>
      <TableTitleRow>
        <TableTitle variant="h4">Fee Schedules</TableTitle>
        {isMediumDisplaySize && (
          // The same grant the desktop button asks for. The compact layout used to gate on identity
          // instead, so the button the desktop refused was one tap away on a phone (issue #4651).
          <NewButton
            aria-label="Create Template"
            color="primary"
            disabled={!sessionUser?.canCreateFeeScheduleTemplates()}
            onClick={() => navigate(`/fee-schedules/${PageTab.Templates}/create`)}
          >
            <AddBoxIcon fontSize="large" />
          </NewButton>
        )}
      </TableTitleRow>
      <LowerTopToolbarContainer>
        <ScrollableTabs
          value={activeTab}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          onChange={handleTabChange}
        >
          <Tab value={PageTab.Templates} label="Templates" />
          <Tab value={PageTab.Active} label="Active" />
          <Tab value={PageTab.Archived} label="Archived" />
        </ScrollableTabs>
      </LowerTopToolbarContainer>
      {isMediumDisplaySize && (
        <MobileToolbarButtonsContainer>
          <GlobalFilterTextField table={table} />
          <ToolbarButtons>
            <ToggleFiltersButton table={table} />
            <ShowHideColumnsButton table={table} />
            <ToggleFullScreenButton table={table} />
          </ToolbarButtons>
        </MobileToolbarButtonsContainer>
      )}
    </TableTopToolbarContainer>
  );
}
